import * as React from 'react'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import type { AllFeatureIds } from 'generated/featureIds.generated'
import {
  ContractsSettingPageDocument,
  WorkingConditionsVersionDetailsDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import { MetaContext } from 'modules/core/lib/navigation/routes'
import type { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { getSettingPageMeta } from 'modules/settings/lib/getSettingPageMeta'

const RootPage = React.lazy(() => import('modules/contracts_settings/pages/RootPage/index'))

const WorkingConditionsRootPage = React.lazy(
  () => import('modules/contracts_settings/pages/WorkingConditionsRootPage')
)

const RedirectToContractSettings = React.lazy(
  () => import('modules/contracts_settings/pages/RedirectToContractSettings')
)

const AggrementApprovals = React.lazy(
  () => import('modules/contracts_settings/pages/AgreementApprovals')
)

const WorkingConditionsVersionPage = React.lazy(
  () => import('modules/contracts_settings/pages/WorkingConditionsVersionPage')
)

const WorkingConditionsDetails = React.lazy(
  () =>
    import('modules/contracts_settings/pages/WorkingConditionsVersionPage/WorkingConditionsVersionDetails')
)

const RedirectToWorkingConditionsDetails = React.lazy(
  () =>
    import('modules/contracts_settings/pages/WorkingConditionsVersionPage/RedirectToWorkingConditionsVersionDetails')
)

const WorkingConditionsVersionEmployees = React.lazy(
  () =>
    import('modules/contracts_settings/pages/WorkingConditionsVersionPage/Employees/WorkingConditionsVersionEmployees')
)

const getWorkingConditionVersionDetailsMeta =
  (navigation: Navigation<NavigationPaths>, ancestorPath: string) =>
  ({ params, session, watchQuery }: MetaContext) => {
    if (!params['identifier']) return { title: '' }
    const versionId = Number(params['identifier'])

    return watchQuery({
      query: WorkingConditionsVersionDetailsDocument,
      variables: {
        companyId: session.company.id,
        versionId: versionId,
      },
    }).map(({ data }) => {
      const peopleAssignment = data.workingConditions.version?.peopleAssignmentsConnection?.nodes[0]

      const jobLevel = peopleAssignment?.jobLevel?.name
      const workplace = peopleAssignment?.workplace?.name

      return {
        title: data.workingConditions.version?.name,
        headerComponent: (
          <ResourceHeader
            title={data.workingConditions.version?.name || ''}
            description={data.workingConditions.version?.description || ''}
            metadata={[
              {
                label: '',
                value: {
                  type: 'status' as const,
                  label: i18n.t(
                    'company_settings.general.employment_data.work_conditions.status.active'
                  ),
                  variant: 'positive' as const,
                },
              },
              !!jobLevel && {
                label: i18n.t(
                  'company_settings.general.employment_data.work_conditions.fields.role.label'
                ),
                value: {
                  type: 'text',
                  content: jobLevel,
                },
              },
              !!workplace && {
                label: i18n.t(
                  'company_settings.general.employment_data.work_conditions.fields.workplace.label'
                ),
                value: {
                  type: 'text',
                  content: workplace,
                },
              },
            ]}
          />
        ),
        subNavigation: [
          {
            title: i18n.t('company_settings.general.employment_data.work_conditions.tabs.details'),
            href: navigation.settings.contracts_settings.workingConditions.version.conditions.fullPath(
              {
                identifier: versionId,
              }
            ),
          },
          {
            title: i18n.t(
              'company_settings.general.employment_data.work_conditions.tabs.employees'
            ),
            href: navigation.settings.contracts_settings.workingConditions.version.employees.fullPath(
              {
                identifier: versionId,
              }
            ),
          },
        ],
        ancestors: [
          {
            title: i18n.t('company_settings.general.employment_data.work_conditions.title'),
            to: ancestorPath,
          },
        ],
      }
    })
  }

const devTimeoffWorkConditionsFeatureId: AllFeatureIds = 'dev_timeoff_work_conditions'

const workingConditionsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      index: true,
      element: <WorkingConditionsRootPage />,
    },
    {
      path: navigation.settings.contracts_settings.workingConditions.version.path,
      element: <WorkingConditionsVersionPage />,
      handle: {
        resource: true,
        meta: getWorkingConditionVersionDetailsMeta(
          navigation,
          navigation.settings.contracts_settings.workingConditions.fullPath()
        ),
      },
      children: [
        {
          index: true,
          element: <RedirectToWorkingConditionsDetails />,
        },
        {
          path: navigation.settings.contracts_settings.workingConditions.version.conditions.path,
          element: <WorkingConditionsDetails />,
        },
        {
          path: navigation.settings.contracts_settings.workingConditions.version.employees.path,
          element: <WorkingConditionsVersionEmployees />,
        },
      ],
    },
  ]
}

const contractSettingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.contracts_settings.path,
      element: <RootPage />,
      handle: {
        resource: true,
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: ContractsSettingPageDocument,
            variables: {
              companyId: session.company.id,
              featureAvailabilityIds: [Features.compensations_management],
              featureIds: [devTimeoffWorkConditionsFeatureId],
            },
          }).map(({ data }) => {
            const settingPageMeta = getSettingPageMeta(navigation, 'contracts')
            const enabledWorkConditions =
              data?.features?.featuresConnection.nodes.some(
                (el) => el.name === 'dev_timeoff_work_conditions' && el.enabled
              ) ?? false

            return {
              title: settingPageMeta?.title,
              headerComponent: (
                <ResourceHeader
                  title={settingPageMeta?.title || ''}
                  description={settingPageMeta?.description || ''}
                />
              ),
              subNavigation: [
                {
                  title: i18n.t('company_settings.general.employment_data.approvals.title'),
                  href: navigation.settings.contracts_settings.agreementApprovals.fullPath(),
                },
                {
                  title: i18n.t('company_settings.general.employment_data.work_conditions.title'),
                  href: navigation.settings.contracts_settings.workingConditions.fullPath(),
                  isVisible: enabledWorkConditions,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToContractSettings />,
        },
        {
          path: navigation.settings.contracts_settings.agreementApprovals.path,
          element: <AggrementApprovals />,
        },
        {
          path: navigation.settings.contracts_settings.workingConditions.path,
          children: workingConditionsRoutes(navigation),
        },
      ],
    },
  ]
}

export default contractSettingsRoutes
